import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { TinyColor } from '@ctrl/tinycolor';
import { ActionSheetController, IonButton } from '@ionic/angular';
import { Store } from '@ngrx/store';

import { has, isNil, map } from 'lodash-es';

import { walkingFarmAvailableLeadStatuses } from '../../../constants/walking-farm.constant';
import {
  WalkingFarmLeadStatusColorEnum,
  WalkingFarmLeadStatusEnum,
  WalkingFarmLeadStatusLabelEnum
} from '../../../enums/walking-farm';
import * as fromWalkingFarmActions from '../../../store/actions/walking-farm.actions';
import { WalkingFarmProperty } from '../../../typings/walking-farm';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-property-lead-status-selector',
  standalone: false,
  templateUrl: './lead-status-selector.component.html'
})
export class LeadStatusSelectorComponent implements OnChanges, AfterViewInit {
  private actionSheetController = inject(ActionSheetController);
  private store = inject(Store);

  @ViewChild(IonButton, { read: ElementRef, static: true }) button?: ElementRef;
  @Input() walkingFarmId: string | null = null;
  @Input() property?: WalkingFarmProperty | null = null;

  color: WalkingFarmLeadStatusColorEnum = WalkingFarmLeadStatusColorEnum.Default;
  status: WalkingFarmLeadStatusLabelEnum = WalkingFarmLeadStatusLabelEnum.Default;

  ngOnChanges(changes: SimpleChanges) {
    if (has(changes, 'property') && !isNil(this.property)) {
      switch (this.property.Status?.toLowerCase()) {
        case WalkingFarmLeadStatusEnum.Lead:
          this.color = WalkingFarmLeadStatusColorEnum.Lead;
          this.status = WalkingFarmLeadStatusLabelEnum.Lead;
          break;
        case WalkingFarmLeadStatusEnum.NotContacted:
          this.color = WalkingFarmLeadStatusColorEnum.NotContacted;
          this.status = WalkingFarmLeadStatusLabelEnum.NotContacted;
          break;
        case WalkingFarmLeadStatusEnum.NotHome:
          this.color = WalkingFarmLeadStatusColorEnum.NotHome;
          this.status = WalkingFarmLeadStatusLabelEnum.NotHome;
          break;
        case WalkingFarmLeadStatusEnum.NotInterested:
          this.color = WalkingFarmLeadStatusColorEnum.NotInterested;
          this.status = WalkingFarmLeadStatusLabelEnum.NotInterested;
          break;
        case WalkingFarmLeadStatusEnum.OnMarket:
          this.color = WalkingFarmLeadStatusColorEnum.OnMarket;
          this.status = WalkingFarmLeadStatusLabelEnum.OnMarket;
          break;
        case WalkingFarmLeadStatusEnum.Prospect:
          this.color = WalkingFarmLeadStatusColorEnum.Prospect;
          this.status = WalkingFarmLeadStatusLabelEnum.Prospect;
          break;
        default:
          this.color = WalkingFarmLeadStatusColorEnum.Default;
          this.status = WalkingFarmLeadStatusLabelEnum.Default;
          break;
      }
      this.updateColors();
    }
  }

  ngAfterViewInit() {
    this.updateColors();
  }

  updateColors() {
    if (this.button) {
      const color = new TinyColor(this.color);
      this.button?.nativeElement.setAttribute(
        'style',
        [
          `--background: ${this.color}`,
          `--background-hover:${this.color}`,
          `--background-activated:${color.lighten(10).toHexString()}`,
          `--background-focused: ${color.lighten(10).toHexString()}`,
          `--color: #fff`
        ].join(';')
      );
    }
  }

  async changeStatus(event: MouseEvent) {
    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();
    const actionSheet = await this.actionSheetController.create({
      buttons: [
        ...map(walkingFarmAvailableLeadStatuses, (item) => ({
          htmlAttributes: {
            style: {
              color: item.color
            }
          },
          role: this.status === item.label ? 'selected' : item.value,
          text: item.label
        })),
        {
          data: {
            action: 'cancel'
          },
          role: 'cancel',
          text: 'Cancel'
        }
      ],
      header: 'Lead Status',
      subHeader: 'Change the status'
    });

    await actionSheet.present();
    const { role } = await actionSheet.onDidDismiss();
    if (
      role &&
      (map(walkingFarmAvailableLeadStatuses, (item) => item.value) as string[]).includes(role) &&
      !isNil(this.walkingFarmId) &&
      !isNil(this.property)
    ) {
      this.store.dispatch(
        fromWalkingFarmActions.updateProperty({
          payload: {
            farmId: this.walkingFarmId,
            property: {
              ...this.property,
              Status: role
            }
          }
        })
      );
    }
  }
}
